/* ===========================================================================
   Chart theme — SHARED FILE
   ---------------------------------------------------------------------------
   Chart.js draws to a canvas, so it cannot read CSS custom properties. This
   module is the single bridge between the design tokens in
   src/styles/docity-tokens.css and every chart in the three dashboards.

   Rules encoded here (do not work around them per-screen):
     • Categorical hues are assigned from SERIES[] in fixed order, never cycled
       and never reordered. Colour follows the entity, not its rank.
     • One y-axis. Never a second scale — two measures of different magnitude
       become two charts or an indexed series.
     • Grid and axes are recessive; the marks carry the message.
     • A single series needs no legend box — the chart title names it. Two or
       more series always get one.
     • ALL-PAIRS FORMS (pie, doughnut, scatter, bubble) are capped at 4 series
       and must carry direct labels. See the note in docity-tokens.css.
   =========================================================================== */

/* Validated categorical palette, fixed order. Mirrors --ds-chart-1..8. */
export const SERIES = [
    '#12968A', // 1 teal — brand, always the first series
    '#D97706', // 2 amber
    '#7C5CD6', // 3 violet
    '#C2415C', // 4 rose
    '#3B76C9', // 5 cerulean
    '#4E9A2F', // 6 green
    '#C74B9E', // 7 magenta
    '#8A6A00', // 8 olive
];

/* Maximum series for forms where any two marks can sit side by side. */
export const ALL_PAIRS_SERIES_CAP = 4;

export const INK = {
    text: '#0D1B2A',
    label: '#3D5B74',
    muted: '#5E7891',
    grid: '#DDE3EA',
    axis: '#BDC8D3',
    surface: '#FFFFFF',
    border: '#DDE3EA',
};

export const FONT_FAMILY =
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/**
 * Hex to rgba, for the translucent area fill under a line.
 */
export const alpha = (hex, a) => {
    const value = hex.replace('#', '');
    const full =
        value.length === 3
            ? value
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : value;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/** Indian-format currency, no decimals. ₹1,24,500 rather than ₹124500. */
export const formatINR = (value) => {
    const number = Number(value) || 0;
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(number);
};

/** Compact currency for axis ticks, where space is tight. ₹1.2L, ₹45K. */
export const formatINRCompact = (value) => {
    const number = Number(value) || 0;
    if (Math.abs(number) >= 10000000) return `₹${(number / 10000000).toFixed(1)}Cr`;
    if (Math.abs(number) >= 100000) return `₹${(number / 100000).toFixed(1)}L`;
    if (Math.abs(number) >= 1000) return `₹${Math.round(number / 1000)}K`;
    return `₹${number}`;
};

export const formatCount = (value) =>
    new Intl.NumberFormat('en-IN').format(Number(value) || 0);

/* ---------------------------------------------------------------------------
   Tooltip — one styling for every chart in the product.
   --------------------------------------------------------------------------- */
const tooltip = (valueFormatter) => ({
    enabled: true,
    backgroundColor: INK.text,
    titleColor: '#FFFFFF',
    titleFont: { family: FONT_FAMILY, size: 12, weight: '600' },
    bodyColor: 'rgba(255,255,255,0.86)',
    bodyFont: { family: FONT_FAMILY, size: 12, weight: '500' },
    padding: 10,
    cornerRadius: 8,
    displayColors: true,
    boxWidth: 8,
    boxHeight: 8,
    boxPadding: 5,
    usePointStyle: true,
    borderWidth: 0,
    callbacks: valueFormatter
        ? {
              label: (ctx) => {
                  const label = ctx.dataset.label ? `${ctx.dataset.label}: ` : '';
                  return label + valueFormatter(ctx.parsed.y ?? ctx.parsed);
              },
          }
        : undefined,
});

/* ---------------------------------------------------------------------------
   Legend — only rendered when a chart carries two or more series.
   --------------------------------------------------------------------------- */
const legend = (show) => ({
    display: show,
    position: 'top',
    align: 'end',
    labels: {
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 16,
        color: INK.label,
        font: { family: FONT_FAMILY, size: 12, weight: '500' },
    },
});

/**
 * Options for a line or area chart.
 *
 * @param seriesCount     how many datasets — drives whether a legend appears
 * @param valueFormatter  formats the tooltip value and the y ticks
 */
export const lineOptions = ({ seriesCount = 1, valueFormatter = formatINRCompact } = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        /* Crosshair behaviour: hovering anywhere in a column reads that column,
           so the hit target is far larger than the 3px point. */
        mode: 'index',
        intersect: false,
    },
    layout: { padding: { top: 4, right: 8, bottom: 0, left: 0 } },
    plugins: {
        legend: legend(seriesCount > 1),
        tooltip: tooltip(valueFormatter),
        title: { display: false },
    },
    elements: {
        line: { borderWidth: 2, tension: 0.32, borderCapStyle: 'round' },
        point: {
            radius: 0,
            /* Grows to a comfortable target only on hover, so a dense series
               is not a wall of dots. */
            hoverRadius: 5,
            hoverBorderWidth: 2,
            hoverBorderColor: INK.surface,
            hitRadius: 12,
        },
    },
    scales: {
        x: {
            border: { display: false },
            grid: { display: false },
            ticks: {
                color: INK.muted,
                font: { family: FONT_FAMILY, size: 11 },
                maxRotation: 0,
                autoSkipPadding: 16,
            },
        },
        /* One axis. Never add a `y1`. */
        y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: INK.grid, drawTicks: false },
            ticks: {
                color: INK.muted,
                font: { family: FONT_FAMILY, size: 11 },
                padding: 8,
                maxTicksLimit: 5,
                callback: (value) => valueFormatter(value),
            },
        },
    },
});

/**
 * Options for a vertical bar chart.
 */
export const barOptions = ({ seriesCount = 1, valueFormatter = formatCount, horizontal = false } = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
        legend: legend(seriesCount > 1),
        tooltip: tooltip(valueFormatter),
        title: { display: false },
    },
    layout: { padding: { top: 4, right: 8, bottom: 0, left: 0 } },
    scales: {
        x: {
            border: { display: false },
            grid: { display: horizontal, color: INK.grid, drawTicks: false },
            ticks: {
                color: INK.muted,
                font: { family: FONT_FAMILY, size: 11 },
                maxRotation: 0,
                autoSkipPadding: 12,
                callback: horizontal ? (value) => valueFormatter(value) : undefined,
            },
        },
        y: {
            beginAtZero: true,
            border: { display: false },
            grid: { display: !horizontal, color: INK.grid, drawTicks: false },
            ticks: {
                color: INK.muted,
                font: { family: FONT_FAMILY, size: 11 },
                padding: 8,
                maxTicksLimit: 5,
                callback: horizontal ? undefined : (value) => valueFormatter(value),
            },
        },
    },
});

/**
 * Options for a doughnut.
 *
 * All-pairs form, so it is capped at ALL_PAIRS_SERIES_CAP slices and always
 * ships a legend carrying the values — the segments are never identified by
 * colour alone.
 */
export const doughnutOptions = ({ valueFormatter = formatCount } = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
        legend: { display: false },
        tooltip: {
            ...tooltip(null),
            callbacks: {
                label: (ctx) => ` ${ctx.label}: ${valueFormatter(ctx.parsed)}`,
            },
        },
    },
    /* 2px of surface between adjacent segments, so touching fills stay legible
       rather than blending into one another. */
    borderColor: INK.surface,
    borderWidth: 2,
});

/**
 * Build a line/area dataset in the correct slot colour.
 *
 * @param slot zero-based index into SERIES. Pass the series' stable position,
 *             never its current rank in the data.
 */
export const lineDataset = (label, data, slot = 0, { fill = true } = {}) => {
    const color = SERIES[slot % SERIES.length];
    return {
        label,
        data,
        borderColor: color,
        backgroundColor: fill ? alpha(color, 0.12) : color,
        pointBackgroundColor: color,
        pointHoverBackgroundColor: color,
        borderWidth: 2,
        fill,
    };
};

/**
 * Build a bar dataset in the correct slot colour.
 */
export const barDataset = (label, data, slot = 0) => {
    const color = SERIES[slot % SERIES.length];
    return {
        label,
        data,
        backgroundColor: color,
        hoverBackgroundColor: color,
        borderColor: 'transparent',
        borderWidth: 0,
        /* Rounded data-end anchored to the baseline. */
        borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
        borderSkipped: 'bottom',
        maxBarThickness: 36,
    };
};

/**
 * Fold a long tail into an "Other" slot rather than inventing a 9th hue, and
 * enforce the all-pairs cap for pie/doughnut/scatter forms.
 *
 * @returns { labels, values, colors }
 */
export const capSeries = (entries, cap = ALL_PAIRS_SERIES_CAP) => {
    const sorted = [...entries].sort((a, b) => b.value - a.value);
    if (sorted.length <= cap) {
        return {
            labels: sorted.map((e) => e.label),
            values: sorted.map((e) => e.value),
            colors: sorted.map((_, i) => SERIES[i]),
        };
    }
    const head = sorted.slice(0, cap - 1);
    const tail = sorted.slice(cap - 1);
    const otherValue = tail.reduce((sum, e) => sum + e.value, 0);
    return {
        labels: [...head.map((e) => e.label), `Other (${tail.length})`],
        values: [...head.map((e) => e.value), otherValue],
        colors: [...head.map((_, i) => SERIES[i]), '#8A9EB1'],
    };
};
